import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import type {
  CoordinatorTemplatesResponse,
  CoordinatorTemplate,
} from './types';

const TWO_MONTHS_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

export const templateKeys = {
  all: ['coordinator', 'templates'] as const,
  published: ['coordinator', 'templates', 'published'] as const,
  byId: (id: number) => ['coordinator', 'templates', id] as const,
};

export const useCoordinatorTemplates = (
  status: 'published' | 'draft' = 'published',
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey:
      status === 'published' ? templateKeys.published : templateKeys.all,
    queryFn: async (): Promise<CoordinatorTemplatesResponse> => {
      return http.get('/coordinator_templates', {
        params: status ? { status } : undefined,
      });
    },
    staleTime: 0,
    select: (data) => (Array.isArray(data?.data) ? data.data : []),
    enabled: options?.enabled ?? true,
  });

/** Fetch single template by id for preview (body). Cached 2 months. */
export const useCoordinatorTemplateById = (
  id: number | null,
  enabled: boolean
) =>
  useQuery({
    queryKey: templateKeys.byId(id!),
    queryFn: async (): Promise<{ data: CoordinatorTemplate }> => {
      const res = await http.get(`/coordinator_templates/${id}`);
      return res as { data: CoordinatorTemplate };
    },
    enabled: enabled && id !== null && id > 0,
    staleTime: TWO_MONTHS_MS,
  });
